/**
 * `DetailPage` - /members/:id render.
 *
 * Composes:
 *   - useAccountQuery (TanStack)
 *   - SoftDeletedBanner (only renders if closed / deleted)
 *   - DetailHeader (breadcrumb + header card)
 *   - a 340px rail: HolderSection (audited PII), LifecycleSection
 *   - a wide column: IdentifiersSection, BankingSection, and
 *     Sm2MetadataSection (collapsed `<details>`)
 *
 * Error states:
 *   - 404 -> in-page NotFoundState ("Account not found")
 *   - 5xx -> in-page ErrorState with correlation id + Retry
 *   - 401 -> handled by the shared BffErrorProvider (mounted in app.tsx)
 *   - 403 -> navigate to /access-denied (also handled by BffErrorProvider)
 *
 * The page constructs its own client (same pattern as `ListPage`) so the
 * MSAL acquirer is captured at render time. Tests pass a `client`
 * override.
 *
 * The detail page receives `id` via `useParams`. A missing id (which
 * react-router would not produce in practice) renders the not-found state
 * defensively.
 */
import { useParams } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import { useMsal } from '@azure/msal-react'

import { apiBaseUrl, apiTokenScopes } from '../../../auth/msal-config'
import { BffError, createBffClient, type BffClient } from '../../../lib/bff-client'
import { useBffError } from '../../../lib/use-bff-error'
import { ErrorState } from '../../../components/error-state'
import { LoadingPanel } from '../../../components/loading-panel'
import { NotFoundState } from '../../../components/not-found-state'
import { SessionExpiredDialog } from '../../../components/session-expired-dialog'
import { BankingSection } from './banking-section'
import { DetailHeader } from './detail-header'
import { HolderSection } from './holder-section'
import { IdentifiersSection } from './identifiers-section'
import { LifecycleSection } from './lifecycle-section'
import { Sm2MetadataSection } from './sm2-metadata-section'
import { SoftDeletedBanner } from './soft-deleted-banner'
import { useAccountQuery } from './use-account-query'

export interface DetailPageProps {
  /** Test-only override; production constructs its own client. */
  readonly client?: BffClient
}

export function DetailPage({ client: clientOverride }: DetailPageProps = {}): JSX.Element {
  const { id } = useParams()
  const { instance, accounts: msalAccounts } = useMsal()
  const bffError = useBffError()

  const client = useMemo<BffClient>(() => {
    if (clientOverride) return clientOverride
    return createBffClient({
      baseUrl: apiBaseUrl,
      acquireToken: async () => {
        const account = msalAccounts[0]
        const result = await instance.acquireTokenSilent(
          account ? { scopes: apiTokenScopes, account } : { scopes: apiTokenScopes },
        )
        return result.accessToken
      },
    })
  }, [clientOverride, instance, msalAccounts])

  // Defensive guard: the route is `/members/:id`; react-router will not
  // resolve without `id`. The fallback ensures the type guard below is
  // sound and avoids `id!` non-null assertions further down.
  if (!id) {
    return <MemberNotFound />
  }

  return <DetailInner accountId={id} client={client} onAuthError={bffError.handle} sessionDialog />
}

function DetailInner({
  accountId,
  client,
  onAuthError,
  sessionDialog,
}: {
  readonly accountId: string
  readonly client: BffClient
  readonly onAuthError: (err: unknown) => void
  readonly sessionDialog: boolean
}): JSX.Element {
  const query = useAccountQuery({ client, accountId })
  const bffError = useBffError()

  // 401 / 403 routing - mirrors ListPage's pattern.
  useEffect(() => {
    if (query.error) {
      onAuthError(query.error)
    }
  }, [query.error, onAuthError])

  if (query.isPending) {
    return (
      <section aria-labelledby="account-detail-loading" className="flex flex-col gap-murmuration-4">
        <h1 id="account-detail-loading" className="sr-only">
          Loading account
        </h1>
        <LoadingPanel label="Loading member" />
      </section>
    )
  }

  if (query.isError) {
    const err = query.error
    const isBffErr = err instanceof BffError
    const status = isBffErr ? err.status : 0
    const correlationId = isBffErr ? err.correlationId : null

    if (status === 404) {
      return <MemberNotFound />
    }
    // 401 / 403 are routed by the provider; this in-page panel covers
    // 5xx + network + 503 (audit-emit failure during the read).
    return (
      <section aria-labelledby="account-detail-error" className="flex flex-col gap-murmuration-4">
        <h1 id="account-detail-error" className="sr-only">
          Could not load account
        </h1>
        <ErrorState
          title={
            status === 503 ? 'Service temporarily unavailable.' : 'Could not load this account.'
          }
          correlationId={correlationId}
          onRetry={() => {
            void query.refetch()
          }}
        />
        {sessionDialog && (
          <SessionExpiredDialog
            open={bffError.sessionExpiredOpen}
            onDismiss={bffError.dismissSessionExpired}
          />
        )}
      </section>
    )
  }

  const account = query.data

  return (
    <section aria-labelledby="account-detail-heading" className="flex flex-col gap-murmuration-4">
      <SoftDeletedBanner account={account} />
      <DetailHeader
        accountId={account.id}
        extAccountId={account.ext_account_id}
        provider={account.provider}
        status={account.status}
        appliedAt={account.applied_at}
      />
      {/*
        Two-column split per the design handoff "Member 360" layout: a 340px
        rail beside the wide column, `align-items: start` so a short card does
        not stretch to match a tall neighbour.

        The rail carries the person - `HolderSection` is the handoff's Profile
        card, the audited click-to-reveal PII block - then the account's
        lifecycle. The wide column carries the record: identifiers, banking
        rails, and the collapsed SM2 projection metadata.
      */}
      <div className="grid grid-cols-1 items-start gap-murmuration-4 xl:grid-cols-[340px_1fr]">
        <div className="flex flex-col gap-murmuration-4">
          <HolderSection account={account} client={client} onAuthError={onAuthError} />
          <LifecycleSection account={account} />
        </div>
        <div className="flex min-w-0 flex-col gap-murmuration-4">
          <IdentifiersSection account={account} client={client} onAuthError={onAuthError} />
          <BankingSection account={account} client={client} onAuthError={onAuthError} />
          <Sm2MetadataSection account={account} />
        </div>
      </div>
      {sessionDialog && (
        <SessionExpiredDialog
          open={bffError.sessionExpiredOpen}
          onDismiss={bffError.dismissSessionExpired}
        />
      )}
    </section>
  )
}

/**
 * Wraps the shared panel with member-detail copy. The message deliberately
 * does not repeat the id that was requested - the MUR "Design Tokens and Accessibility" page (content rules).
 */
function MemberNotFound(): JSX.Element {
  return (
    <NotFoundState
      heading="Member not found"
      description="No member record exists for the id you opened. It may have been closed, or the link may be out of date."
    />
  )
}
